import React, { Fragment, useState } from "react"
import { Link, useNavigate } from "react-router"
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Modal, Dropdown } from 'react-bootstrap'

import { SET_ACCOUNT } from 'store/account/action'
import { getDefaultHomePath } from 'routes/index'
import { httpOk, GUESTGET, GUESTPOST, GUESTPUT } from 'helpers/api'
import { getDeviceId } from 'helpers/clientHeaders'
import { formatMoney, formatMoneyInput, regexLogin, findKeyFromArrayByValue } from 'helpers/helpers'
import { APP_GROUPS, SUPPORT } from 'helpers/apps'
import logo from 'assets/images/logo_icon.png'

function Login() {
	const { i18n, t } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const [user, setUser] = useState({ "login": "", "password": "" })
	const [modalPayment, setModalPayment] = useState({ "bool": false, "data": {}, activeTab: 1 })
	const [modalApps, setModalApps] = useState(false)
	const [versions, setVersions] = useState({})
	const [copiedIndex, setCopiedIndex] = useState(null);
	const [showPassword, setShowPassword] = useState(false)
	const [layoutHint, setLayoutHint] = useState(false)
	const [capsLock, setCapsLock] = useState(false)
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)

	const sanitizeLoginInput = (value = '') => regexLogin(value)

	const hasCyrillicInput = (value = '') => /[а-яА-ЯёЁ]/.test(String(value))

	function detectWrongKeyboardLayout(value = '', event = null) {
		if (hasCyrillicInput(value)) {
			return true
		}

		if (event?.key && event.key.length === 1 && hasCyrillicInput(event.key)) {
			return true
		}

		if (event?.data && hasCyrillicInput(event.data)) {
			return true
		}

		return false
	}

	function updateLogin(value, event = null) {
		if (detectWrongKeyboardLayout(value, event)) {
			setLayoutHint(true)
		} else if (sanitizeLoginInput(value)) {
			setLayoutHint(false)
		}

		if (error) setError('')
		setUser((prev) => ({ ...prev, login: sanitizeLoginInput(value) }))
	}

	function updatePassword(value) {
		if (error) setError('')
		setUser((prev) => ({ ...prev, password: value }))
	}

	function detectCapsLock(event) {
		if (typeof event?.getModifierState !== 'function') return
		setCapsLock(event.getModifierState('CapsLock'))
	}

	async function handleSubmit(e = null) {
		if (e) e.preventDefault();
		if (loading) return

		const login = user.login.trim().toLowerCase()
		// Пароль клиенту чаще всего присылают в сообщении, и вместе с ним копируется
		// хвостовой пробел. Логин мы уже тримим, а пароль уходил как есть — сервер
		// отвечал «Неверный логин или пароль», и понять причину со стороны было нельзя.
		const password = user.password.trim()
		if (!login || !password) {
			setError(t('fill_login_and_password'))
			return
		}

		// device_id — постоянный id браузера: по нему журнал «Входы» закрывает сеанс при выходе
		const sendData = {
			login,
			password,
			device_id: getDeviceId(),
			app_code: 'web',
		}

		setError('')
		setLoading(true)
		const response = await GUESTPOST('/login', sendData, { notification: false })
		setLoading(false)

		if (!response) {
			setError(t('no_server_connection'))
			return
		}

		if (!httpOk(response)) {
			if (response?.data?.code === -1) {
				const paymentData = { ...response.data, amount: response?.data?.tariff }
				setModalPayment({ ...modalPayment, bool: true, data: paymentData })
				return
			}

			setError(response?.data?.message || t('login_or_password_invalid'))
			return
		}

		const data = response?.data
		if (!data?.token || !data?.role) {
			setError(data?.message || t('login_or_password_invalid'))
			return
		}

		// Права передаём из ответа: в redux аккаунт попадёт ниже, а домашний раздел
		// администратора зависит от того, что ему разрешено.
		const routeAfterLogin = getDefaultHomePath(data.role, data)
		if (routeAfterLogin === '/auth/login') {
			setError(t('you_do_not_have_access'))
			return
		}

		localStorage.setItem('login', login)
		localStorage.setItem('role', data.role)
		localStorage.setItem('token', data.token)

		dispatch(SET_ACCOUNT({
			...data,
			main_user_id: data.id,
		}))

		navigate(routeAfterLogin, { replace: true })
	}

	async function createInvoice() {
		var sendData = {
			login: user.login.toLowerCase(),
			id: modalPayment.data.pos_id,
			amount: modalPayment.data.amount,
			phone: `998${modalPayment.data.phone}`,
			pos_payment_type_id: 1,
		}

		var response = await GUESTPOST('/payment/pos-invoice', sendData, { loader: true })

		if (!httpOk(response)) {
			if (response.data.code === -1) {
				setModalPayment({ ...modalPayment, 'bool': true, 'data': response?.data })
			}
		}
	}

	async function activateCredit() {
		var sendData = {
			'login': modalPayment.data.login,
			'pos_id': modalPayment.data.pos_id
		}

		var response = await GUESTPUT('/pos/activate-credit', sendData, { loader: true })
		if (httpOk(response)) handleSubmit()
	}

	function handleChange(e) {
		if (Number(e.target.value.replace(/[^0-9.]/g, '') && e.target.name === 'amount') > 50000000) return

		var modalPaymentCopy = { ...modalPayment }
		modalPayment.data[e.target.name] = e.target.value.replace(/[^0-9.]/g, '')

		setModalPayment(modalPaymentCopy)
	}

	function toggleModalPayment(bool) {
		setModalPayment({ ...modalPayment, 'bool': bool })
	}

	async function toggleModalApps(bool) {
		if (bool) {
			var response = await GUESTGET('/application-versions', {}, { loader: true })
			if (httpOk(response)) setVersions(response.data)
		}

		setModalApps(bool)
	}

	function handleCopy(text, index) {
		navigator.clipboard.writeText(text) // Копируем текст в буфер обмена
			.then(() => {
				setCopiedIndex(index); // Устанавливаем индекс кнопки
				setTimeout(() => setCopiedIndex(null), 1000); // Убираем уведомление через 2 секунды
			})
			.catch((error) => {
				console.error("Ошибка копирования: ", error);
			});
	}

	function changeLanguage(language = 'uz_latn') {
		i18n.changeLanguage(language);

		if (language === 'ru') {
			localStorage.setItem('lang', 'ru');
		}
		if (language === 'uz_latn') {
			localStorage.setItem('lang', 'uz_latn');
		}
		if (language === 'uz_cyrl') {
			localStorage.setItem('lang', 'uz_cyrl');
		}
		if (language === 'tr') {
			localStorage.setItem('lang', 'tr');
		}
	}

	function getCurrentLocale() {
		const locale = i18n.language
		if (locale === 'ru') return { lang: 'russian' }
		else if (locale === 'uz_latn') return { lang: 'uzbek_latn' }
		else if (locale === 'uz_cyrl') return { lang: 'uzbek_cyrl' }
		else if (locale === 'tr') return { lang: 'turkish' }
		return { lang: 'russian' }
	}

	const LanguageToggle = React.forwardRef(({ children, onClick }, ref) => (
		<span ref={ref} onClick={(e) => { e.preventDefault(); onClick(e); }}>
			{children}
		</span>
	));

	return (
		<>
			<div className="auth-page">
				<div className="auth-box">
					<div className="auth-box-head">
						<div className="auth-brand">
							<img src={logo} alt="Mison" />
							<span>Mison</span>
						</div>

						<Dropdown align="end">
							<Dropdown.Toggle as={LanguageToggle}>
								<span className="auth-lang">
									<i className="uil uil-globe"></i>
									<span>{t(getCurrentLocale().lang)}</span>
									<i className="uil uil-angle-down"></i>
								</span>
							</Dropdown.Toggle>

							<Dropdown.Menu>
								{i18n.language !== 'ru' &&
									<Dropdown.Item onClick={() => changeLanguage("ru")}>
										<span>{t('russian')}</span>
									</Dropdown.Item>
								}
								{i18n.language !== 'uz_latn' &&
									<Dropdown.Item onClick={() => changeLanguage("uz_latn")}>
										<span>{t('uzbek_latn')}</span>
									</Dropdown.Item>
								}
								{i18n.language !== 'uz_cyrl' &&
									<Dropdown.Item onClick={() => changeLanguage("uz_cyrl")}>
										<span>{t('uzbek_cyrl')}</span>
									</Dropdown.Item>
								}
								{i18n.language !== 'tr' &&
									<Dropdown.Item onClick={() => changeLanguage("tr")}>
										<span>{t('turkish')}</span>
									</Dropdown.Item>
								}
							</Dropdown.Menu>
						</Dropdown>
					</div>

					<h1 className="auth-title">{t('logging_in')}</h1>
					<div className="auth-subtitle">{t('auth_subtitle')}</div>

					<form autoComplete="off" onSubmit={(e) => handleSubmit(e)}>
						{error &&
							<div className="auth-alert">
								<i className="uil uil-exclamation-circle"></i>
								<span>{error}</span>
							</div>
						}

						<div className="auth-field">
							<label className="auth-label" htmlFor="auth-login">{t('login')}</label>
							<div className="auth-input-wrap">
								<i className="uil uil-user"></i>
								<input className={`auth-input ${error ? 'is-invalid' : ''}`}
									id="auth-login"
									name="login"
									autoFocus
									lang="en"
									inputMode="text"
									spellCheck={false}
									autoComplete="off"
									value={user.login}
									onChange={(e) => updateLogin(e.target.value, e)}
									onInput={(e) => updateLogin(e.target.value, e)}
									onKeyDown={(e) => {
										if (detectWrongKeyboardLayout('', e)) {
											setLayoutHint(true)
										}
									}}
									onBeforeInput={(e) => {
										if (detectWrongKeyboardLayout(e.data || '', e)) {
											setLayoutHint(true)
										}
									}}
									onCompositionEnd={(e) => updateLogin(e.target.value, e)}
									onPaste={(e) => {
										e.preventDefault()
										const pastedText = e.clipboardData.getData('text')
										if (detectWrongKeyboardLayout(pastedText, e)) {
											setLayoutHint(true)
										}
										updateLogin(pastedText, e)
									}} />
							</div>
							{layoutHint &&
								<div className="auth-hint">
									<i className="uil uil-keyboard"></i>
									<span>{t('login_latin_keyboard_hint')}</span>
								</div>
							}
						</div>

						<div className="auth-field">
							<label className="auth-label" htmlFor="auth-password">{t('password')}</label>
							<div className="auth-input-wrap">
								<i className="uil uil-lock-alt"></i>
								<input className={`auth-input auth-input-password ${error ? 'is-invalid' : ''}`}
									id="auth-password"
									name="password"
									type={showPassword ? "text" : "password"}
									autoComplete="off"
									value={user.password}
									onChange={(e) => updatePassword(e.target.value)}
									onKeyUp={(e) => detectCapsLock(e)}
									onKeyDown={(e) => detectCapsLock(e)} />
								<button type="button"
									className="auth-eye"
									tabIndex={-1}
									title={showPassword ? t('hide_password') : t('show_password')}
									aria-label={showPassword ? t('hide_password') : t('show_password')}
									onClick={() => setShowPassword(!showPassword)}>
									<i className={`uil ${showPassword ? 'uil-eye-slash' : 'uil-eye'}`}></i>
								</button>
							</div>
							{capsLock &&
								<div className="auth-hint">
									<i className="uil uil-arrow-up"></i>
									<span>{t('caps_lock_on')}</span>
								</div>
							}
						</div>

						<button type="submit" className="auth-submit" disabled={loading}>
							{loading &&
								<span className="spinner-border spinner-border-sm" role="status"></span>
							}
							{loading ? t('signing_in') : t('enter')}
						</button>

						<Link to="/auth/forgot-password" className="auth-link">{t('forgot_password')}</Link>
					</form>

					<div className="auth-box-foot">
						<button type="button" className="auth-foot-btn" onClick={() => toggleModalApps(true)}>
							<i className="uil uil-download-alt"></i>
							<span>{t('applications')}</span>
						</button>
						<a className="auth-foot-btn" href={`tel:${SUPPORT.phone}`}>
							<i className="uil uil-headphones-alt"></i>
							<span>{SUPPORT.phone_formatted}</span>
						</a>
					</div>
				</div>

				<div className="auth-legal">
					{t('made_by_as')} <br />
					{t('made_by_as2')} <br />
					<Link to="/auth/map">{t('made_by_as3')}</Link>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={modalPayment.bool} animation={false} size="lg"
				dialogClassName="update-modal-width" centered
				onHide={() => toggleModalPayment(false)}>
				<Modal.Header closeButton>
					<h3>Оплата</h3>
				</Modal.Header>
				<Modal.Body>

					<div className="d-flex justify-content-between gap-3">
						<div className="d-flex flex-column gap-2 fz-18">
							<div className={`tab ${modalPayment.activeTab === 1 ? 'active' : ''}`}
								onClick={() => setModalPayment({ ...modalPayment, activeTab: 1 })}>
								Выставить счет
							</div>
							<div className={`tab ${modalPayment.activeTab === 3 ? 'active' : ''}`}
								onClick={() => setModalPayment({ ...modalPayment, activeTab: 3 })}>
								Доверительный платеж
							</div>
						</div>

						<div className="w-100" style={{ 'height': '300px' }}>
							<div className="h-100 vertical-between">
								<div>
									<div className="d-flex justify-content-between">
										<div>{t('pos')}</div>
										<div>{modalPayment.data.pos_name}</div>
									</div>

									<div className="d-flex justify-content-between">
										<div>{t('id')}</div>
										<div>{modalPayment.data.pos_id}</div>
									</div>

									<div className="d-flex justify-content-between">
										<div>{t('tariff')}</div>
										<div>{formatMoney(modalPayment.data.tariff)}</div>
									</div>

									<div className="d-flex justify-content-between">
										<div>{t('balance2')}</div>
										<div>{formatMoney(modalPayment.data.balance)}</div>
									</div>

									<hr />
								</div>
								{modalPayment.activeTab === 1 &&
									<div className="d-flex flex-column gap-2">
										<input type="text" className="form-control"
											autoComplete="off"
											name="amount"
											placeholder={t('amount')}
											value={formatMoneyInput(modalPayment.data.amount)}
											onChange={(e) => handleChange(e)}
										/>

										<div className="input-group">
											<span className="input-group-text">998</span>
											<input type="text" className="form-control"
												autoComplete="off"
												name="phone"
												value={(modalPayment.data.phone)}
												onChange={(e) => handleChange(e)} />
										</div>

										<button type="button" className="btn btn-primary w-100"
											onClick={() => createInvoice()}>
											{t('pay')}
										</button>
									</div>
								}

								{modalPayment.activeTab === 3 &&
									<div>
										<div>
											<p className="fz-16">
												*После активации “доверительный платеж”
												не забудьте оплатить абоненскую плату, во избежания блокировки входа в систему.
											</p>
											{(modalPayment.data.code === -1 && modalPayment.data.show_credit_button) &&
												<div className="d-flex mt-2">
													<div className="form-check">
														<input type="checkbox" className="form-check-input"
															onClick={(e) => setModalPayment({ ...modalPayment, 'agree': e.target.checked })}
															checked={modalPayment.agree} />
													</div>
													<span className="text-primary fz-16 fw-500">Я согласен</span>
												</div>
											}
										</div>
										{(modalPayment.data.code === -1 && !modalPayment.data.show_credit_button) &&
											<p className="text-center text-danger">Эту функцию можно активировать с 1 по 3 числа каждого календарного месяца</p>
										}
										{(modalPayment.data.code === -1 && modalPayment.data.show_credit_button) &&
											<Fragment>
												<div className="text-center mt-2">
													<button type="button" className="btn btn-primary w-75"
														disabled={(modalPayment.credit_date || !modalPayment.agree)}
														onClick={activateCredit}>
														Активировать
													</button>
												</div>
												<p className="text-center">Активировать доверительный <br />платеж на 1 день</p>
											</Fragment>
										}
									</div>
								}
							</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL */}

			{/* MODAL */}
			<Modal show={modalApps} animation={false}
				centered dialogClassName="auth-apps-modal"
				onHide={() => toggleModalApps(false)}>
				<Modal.Header closeButton>
					<h4>{t('applications')}</h4>
				</Modal.Header>
				<Modal.Body className="auth-apps-columns">
					{APP_GROUPS.map((group, groupIndex) => (
						<div className="auth-apps-group" key={groupIndex}>
							<div className="auth-apps-title">{t(group.title)}</div>

							{group.items.map((item, itemIndex) => {
								const version = item.type ? findKeyFromArrayByValue(versions, 'type', item.type, 'version') : ''
								const itemKey = `${groupIndex}-${itemIndex}`

								return (
									<div className="auth-apps-row" key={itemKey}>
										<div className="auth-apps-name">
											{item.name}
											{version && <span className="auth-apps-version">{version}</span>}
										</div>

										{item.action === 'copy' ?
											<div className="position-relative">
												<button type="button" className="btn btn-sm btn-outline-primary auth-apps-action"
													onClick={() => handleCopy(item.url(version), itemKey)}>
													<i className="uil uil-link"></i> {t('copy')}
												</button>
												{copiedIndex === itemKey &&
													<div className="auth-copied">{t('copied')}</div>
												}
											</div>
											:
											<a className="btn btn-sm btn-outline-primary auth-apps-action"
												href={item.url(version)}>
												<i className="uil uil-download-alt"></i> {t('download')}
											</a>
										}
									</div>
								)
							})}
						</div>
					))}
				</Modal.Body>
			</Modal>
			{/* MODAL */}
		</>
	)
}

export default Login;
